import { AsyncPipe } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  inject,
  OnDestroy,
  OnInit,
} from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormControl,
  ValidatorFn,
  Validators,
  ReactiveFormsModule,
} from '@angular/forms';
import {
  MAT_DIALOG_DATA,
  MatDialogModule,
  MatDialogRef,
} from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { BehaviorSubject, Subject, takeUntil } from 'rxjs';
import { User } from 'src/app/models/user.model';
import { OktaUserService } from '../services/okta-user';

type PwReqState = {
  length: boolean;
  lower: boolean;
  upper: boolean;
  number: boolean;
  symbol: boolean;
  noUsernamePart: boolean;
  noFirstName: boolean;
  noLastName: boolean;
};

function passwordsMatchValidator(group: AbstractControl) {
  const newPw = group.get('newPassword')?.value as string;
  const conf = group.get('confirm')?.value as string;
  return newPw && conf && newPw !== conf ? { mismatch: true } : null;
}

function tokenizeUsername(u?: string): string[] {
  if (!u) return [];
  const local = u.includes('@') ? u.split('@')[0] : u;
  return local.split(/[^A-Za-z0-9]+/).filter((t) => t && t.length >= 3);
}

function passwordPolicyValidatorFactory(ctx: {
  usernameTokens: string[];
  firstName?: string;
  lastName?: string;
}): ValidatorFn {
  return (control: AbstractControl) => {
    const inputValue: string = (control.value as string) || '';
    if (!inputValue) return null;

    const lowerVal = inputValue.toLowerCase();
    const first = (ctx.firstName || '').toLowerCase();
    const last = (ctx.lastName || '').toLowerCase();

    const errors: Record<string, true> = {};
    if (inputValue.length < 8) errors.minLength = true;
    if (!/[a-z]/.test(inputValue)) errors.lowercase = true;
    if (!/[A-Z]/.test(inputValue)) errors.uppercase = true;
    if (!/\d/.test(inputValue)) errors.number = true;
    if (!/[^A-Za-z0-9]/.test(inputValue)) errors.symbol = true;

    if (first && first.length >= 3 && lowerVal.includes(first))
      errors.containsFirstName = true;
    if (last && last.length >= 3 && lowerVal.includes(last))
      errors.containsLastName = true;

    if (ctx.usernameTokens?.length) {
      if (
        ctx.usernameTokens.some(
          (t) => t && t.length >= 3 && lowerVal.includes(t.toLowerCase()),
        )
      ) {
        errors.containsUsername = true;
      }
    }

    return Object.keys(errors).length ? errors : null;
  };
}

@Component({
  selector: 'app-change-password-dialog',
  templateUrl: 'change-password-dialog.component.html',
  imports: [
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatButtonModule,
    MatDialogModule,
    AsyncPipe,
    MatProgressSpinnerModule,
  ],
})
export class ChangePasswordDialogComponent implements OnInit, OnDestroy {
  readonly dialogRef = inject(MatDialogRef<ChangePasswordDialogComponent>);

  readonly dialogData = inject<User | null>(MAT_DIALOG_DATA);

  hideOld = true;

  hideNew = true;

  hideConf = true;

  isSubmitting$ = new BehaviorSubject(false);

  errorMessage: string = null;

  form = this.fb.group(
    {
      oldPassword: new FormControl('', {
        nonNullable: true,
        validators: [Validators.required],
      }),
      newPassword: new FormControl('', { nonNullable: true, validators: [] }),
      confirm: new FormControl('', {
        nonNullable: true,
        validators: [Validators.required],
      }),
    },
    { validators: passwordsMatchValidator },
  );

  private destroy$ = new Subject<void>();

  hasSubmissionError$ = new BehaviorSubject(false);

  constructor(
    private fb: FormBuilder,
    private oktaUserService: OktaUserService,
    private changeDetectorRef: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    const firstName = this.dialogData?.FirstName || '';
    const lastName = this.dialogData?.LastName || '';
    const username =
      this.dialogData?.OktaUsername__c || this.dialogData?.Email || '';
    const usernameTokens = tokenizeUsername(username);

    const policyValidator = passwordPolicyValidatorFactory({
      usernameTokens,
      firstName,
      lastName,
    });
    this.form.controls.newPassword.addValidators([
      Validators.required,
      policyValidator,
      Validators.minLength(8),
    ]);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // Live checklist computed from current value
  get req(): PwReqState {
    const v = this.form.controls.newPassword.value || '';
    const lower = v.toLowerCase();

    const first = (this.dialogData?.FirstName || '').toLowerCase();
    const last = (this.dialogData?.LastName || '').toLowerCase();
    const username = (
      this.dialogData?.OktaUsername__c ||
      this.dialogData?.Email ||
      ''
    ).toLowerCase();
    const usernameTokens = tokenizeUsername(username);

    return {
      length: v.length >= 8,
      lower: /[a-z]/.test(v),
      upper: /[A-Z]/.test(v),
      number: /\d/.test(v),
      symbol: /[^A-Za-z0-9]/.test(v),
      noUsernamePart: !usernameTokens.some(
        (t) => t.length >= 3 && lower.includes(t.toLowerCase()),
      ),
      noFirstName: !(
        v.length > 0 &&
        first &&
        first.length >= 3 &&
        lower.includes(first)
      ),
      noLastName: !(
        v.length > 0 &&
        last &&
        last.length >= 3 &&
        lower.includes(last)
      ),
    };
  }

  submit(): void {
    if (this.form.invalid) return;

    const payload = {
      oldPassword: this.form.controls.oldPassword.value,
      newPassword: this.form.controls.newPassword.value,
    };

    this.isSubmitting$.next(true);

    this.oktaUserService
      .changePassword(payload)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.isSubmitting$.next(false);
          this.hasSubmissionError$.next(false);

          this.dialogRef.close(payload);
        },
        error: (error) => {
          console.error('Error inviting user:', error);
          this.getErrorMessage(error);
        },
      });
  }

  private getErrorMessage(error: { message: string }): void {
    this.hasSubmissionError$.next(true);
    this.isSubmitting$.next(false);
    this.errorMessage = error.message || 'An error occurred. Please try again.';
    this.changeDetectorRef.detectChanges();
  }
}
